import { useEffect, useRef } from 'react'
import Link from 'next/link'
import useSWRInfinite from 'swr/infinite'
import { motion } from 'framer-motion'
import { CheckCircle, ExternalLink } from 'lucide-react'
import { useTranslations } from 'next-intl'

import type { Asset } from '@/lib/types'
import { cn } from '@/lib/utils'
import { useEditorStore } from '@/hooks/use-editor-store'
import { useProjectStore } from '@/hooks/use-project-store'

type PixabayVideo = {
  id: number
  pageURL: string
  type: string
  tags: string
  duration: number
  videos: {
    large: {
      url: string
      width: number
      height: number
      size: number
      thumbnail: string
    }
    medium: {
      url: string
      width: number
      height: number
      size: number
      thumbnail: string
    }
    small: {
      url: string
      width: number
      height: number
      size: number
      thumbnail: string
    }
    tiny: {
      url: string
      width: number
      height: number
      size: number
      thumbnail: string
    }
  }
  views: number
  downloads: number
  likes: number
  comments: number
  user_id: number
  user: string
  userImageURL: string
}

type PixabaySearchResponse = {
  hits: PixabayVideo[]
  total: number
}

const PER_PAGE = 12

interface VideoLibraryProps {
  query: string
  selectedAssets: Asset[]
  onSelect: (e: React.MouseEvent, asset: Asset) => void
  limit?: number
  isExpanded?: boolean
}

export const VideoLibrary = ({
  query,
  selectedAssets,
  onSelect,
  limit,
  isExpanded = false,
}: VideoLibraryProps) => {
  const t = useTranslations('video-library')
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { setIsToolboxOpen } = useProjectStore()
  const {
    getIsEditorOn,
    getIsTargetImage,
    getIsTargetVideo,
    setTargetSrc,
    setTargetSrcWithType,
  } = useEditorStore()
  const isEditorOn = getIsEditorOn()
  const isImageSelectedInEditor = isEditorOn && getIsTargetImage()
  const isVideoSelectedInEditor = isEditorOn && getIsTargetVideo()
  // Allow media type switching: video can be selected when image is selected
  const isMediaSelectedInEditor =
    isImageSelectedInEditor || isVideoSelectedInEditor

  const getKey = (
    pageIndex: number,
    previousPageData: PixabaySearchResponse | null,
  ) => {
    if (limit && pageIndex > 0) return null // Only fetch first page if limit is set
    if (previousPageData && !previousPageData.hits.length) return null
    const searchParam = query ? `&q=${encodeURIComponent(query)}` : ''
    return `/api/media/pixabay?type=video&page=${pageIndex + 1}&per_page=${limit || PER_PAGE}${searchParam}`
  }

  const { data, size, setSize, isLoading } =
    useSWRInfinite<PixabaySearchResponse>(
      getKey,
      async (url) => {
        const res = await fetch(url)
        if (!res.ok) throw new Error('Failed to fetch')
        return res.json()
      },
      {
        revalidateFirstPage: false,
      },
    )

  const allVideos = data ? data.flatMap((page) => page.hits) : []
  const videos = limit
    ? allVideos
        .filter((video) => video.videos?.medium?.thumbnail)
        .slice(0, limit)
    : allVideos.filter((video) => video.videos?.medium?.thumbnail)
  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = !data?.[0] || data[0].hits.length === 0
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1].hits.length < PER_PAGE)

  useEffect(() => {
    if (!loadMoreRef.current || isLoadingMore || isReachingEnd || limit) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSize(size + 1)
        }
      },
      { rootMargin: '50px' },
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [isLoadingMore, isReachingEnd, setSize, size, limit])

  const handleVideoSelect = (e: React.MouseEvent, video: PixabayVideo) => {
    const assetData: Asset = {
      source: 'elements',
      type: 'video',
      link: video.videos.medium.url,
      thumbnail: video.videos.medium.thumbnail,
    }

    // Always call onSelect first (for modal/parent component usage)
    onSelect(e, assetData)

    // If media is selected in editor (not in modal), also update the target directly
    if (isMediaSelectedInEditor) {
      setTargetSrcWithType(video.videos.medium.url, 'video')
      setIsToolboxOpen(false)
    }
  }

  if (isLoadingMore && size === 1) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('no_videos')}</p>
      </div>
    )
  }

  return (
    <>
      {videos.map((video) => (
        <motion.div
          key={video.id}
          className={cn(
            'flex-none relative aspect-video',
            isExpanded ? 'w-full' : 'w-[200px]',
            'cursor-pointer',
          )}
          style={isExpanded ? { paddingTop: '56.25%' } : {}}
          onClick={(e) => handleVideoSelect(e, video)}
        >
          <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden group">
            <img
              src={video.videos.medium.thumbnail}
              alt={video.tags}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />
          </div>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
            <p className="text-xs text-white line-clamp-2">
              {t('video_info', {
                user: video.user,
                duration: Math.round(video.duration),
              })}
            </p>
          </div>
          {selectedAssets.some(
            (asset) => asset.link === video.videos.medium.url,
          ) && (
            <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
              <CheckCircle className="w-4 h-4" />
            </div>
          )}
          <div className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Link
              href={video.videos.medium.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-300 hover:text-white transition-colors"
              onClick={(e) => e.stopPropagation()}
              title={t('open_in_new')}
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      ))}
      {!limit && <div ref={loadMoreRef} className="h-4" />}
      {isLoadingMore && !limit && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('loading_more')}
          </p>
        </div>
      )}
    </>
  )
}
